// packages/console/src/components/panels/LiveProgramMonitor.tsx
//
// Program monitor for the LIVE console. A 16:9 on-air preview that plays the
// REAL transcoded live output (the HLS the pipeline pushes to the object store),
// with an on-air badge and a burnt-in session timecode.
//
// When a session is on air and its stream id is known, one hls.js instance loads
// the live master playlist through the authenticated live proxy
// (/api/live/.../hls/index.m3u8): a single bearer token authorises the manifest
// AND every relative variant/segment request, so the browser plays the live
// output without a bucket credential. The playlist is produced a beat after the
// encoder starts, so a 404 during warm-up is expected: the player retries the
// manifest until the first segments land, and a veil reads "Acquiring signal"
// until frames arrive.
//
// When the session is not on air, the honest empty state (R10) holds: no frames,
// so the preview veils to "No program" and the timecode reads an em-dash rather
// than counting up from nothing.

import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Dot } from "../Parts";
import { EM_DASH, fmtDur } from "../../lib/format";
import { servicePaths } from "../../api/config";
import { getAccessToken } from "../../api/session";
import { rollingKbps, fmtKbps, type BitrateSample } from "../../lib/bitrate";
import type { LiveStream } from "../../api/live";
import { mintPlaybackUrl } from "../../api/liveControl";
import { connectWhep, whepUrlFromPlayback, llHlsUrlFromWhep, type WhepSession } from "../../api/whep";

// timecode renders the session timecode (tcSeconds) as HH:MM:SS, or an em-dash
// when there is no session or the figure is absent.
function timecode(stream: LiveStream | null): string {
  if (!stream || stream.tcSeconds === null) return EM_DASH;
  return fmtDur(stream.tcSeconds);
}

// liveHlsUrl builds the authenticated live-proxy URL for a stream's master
// playlist. hls.js resolves its relative variant/segment requests against this
// base, so the whole live tree is served under one token by the live-ingest
// proxy (GET /v1/live-streams/{id}/hls/{file...}).
function liveHlsUrl(streamId: string): string {
  return `${servicePaths.live}/v1/live-streams/${encodeURIComponent(streamId)}/hls/index.m3u8`;
}

// authXhrSetup attaches the bearer token to the manifest request and to every
// relative variant/segment request, so the authenticated live proxy serves the
// whole tree under one token (the live analogue of the VOD player's xhrSetup).
function authXhrSetup(xhr: XMLHttpRequest) {
  const token = getAccessToken();
  if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
}

export function LiveProgramMonitor({ stream, streamId }: { stream: LiveStream | null; streamId?: string | null }) {
  const onAir = stream?.state === "on_air";
  // A stream that failed during setup never went on air: instead of the neutral
  // "No program" veil, surface the failure with its reason so an operator sees WHY
  // (a dead preset, a reservation failure). error may be empty if the reason did
  // not travel, so fall back to a generic line.
  const failed = stream?.state === "failed";
  const failureReason = (stream?.error ?? "").trim() || "Broadcast setup failed";
  const tc = timecode(stream);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // acquiring is true from the moment the player mounts until the first frame is
  // rendered, so the veil covers the warm-up window (manifest not yet produced,
  // segments not yet buffered) instead of showing a black rectangle.
  const [acquiring, setAcquiring] = useState(true);
  // Live delivered bitrate (rolling avg of buffered fragments), painted as a small
  // corner badge like the standalone player. null until the first fragment buffers.
  const [kbps, setKbps] = useState<number | null>(null);
  const canPlay = onAir && !!streamId && Hls.isSupported();
  // WebRTC (WHEP) is the sub-500ms path, tried FIRST; on any failure (unsupported
  // browser, ICE failure, at-capacity) the monitor auto-falls-back to HLS. mode is
  // reset to "webrtc" whenever a fresh session starts so every broadcast prefers
  // low latency.
  const canWebrtc = onAir && !!streamId && typeof RTCPeerConnection !== "undefined";
  const [mode, setMode] = useState<"webrtc" | "hls">("webrtc");
  useEffect(() => {
    if (onAir && streamId) setMode("webrtc");
  }, [streamId, onAir]);

  // WebRTC playback: mint a playback token (same token as HLS), derive the WHEP URL,
  // and connect. Success clears the acquiring veil; any failure flips to HLS.
  useEffect(() => {
    if (!canWebrtc || mode !== "webrtc") return;
    const video = videoRef.current;
    if (!video) return;
    setAcquiring(true);
    setKbps(null);
    let cancelled = false;
    let session: WhepSession | null = null;
    (async () => {
      try {
        const pb = await mintPlaybackUrl(streamId!);
        const whep = whepUrlFromPlayback(streamId!, pb.url);
        if (!whep) throw new Error("no whep url");
        if (cancelled) return;
        session = await connectWhep(video, whep);
        if (cancelled) { session.close(); return; }
        setAcquiring(false);
      } catch {
        if (!cancelled) setMode("hls");
      }
    })();
    return () => {
      cancelled = true;
      session?.close();
      if (video) video.srcObject = null;
    };
  }, [canWebrtc, streamId, mode]);

  // One hls.js instance per on-air session. It loads the live master and, because
  // the playlist appears a beat after the encoder starts, retries the manifest on
  // a fatal load error (a warm-up 404) by rebuilding after a short delay for as
  // long as the session stays on air. Rebuilt/destroyed only when onAir or the
  // stream id changes, so a steady stream is never torn down mid-play.
  useEffect(() => {
    if (!canPlay || mode !== "hls") return;
    const video = videoRef.current;
    if (!video) return;

    setAcquiring(true);
    setKbps(null);
    const samples: BitrateSample[] = [];
    let cancelled = false;
    let hls: Hls | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const build = (src: string, tier: "llhls" | "s3") => {
      if (cancelled) return;
      hls = new Hls({
        xhrSetup: authXhrSetup,
        lowLatencyMode: true,
        // Keep the player near the live edge: a small back-buffer and a short
        // live-sync window trim latency for a monitor (not a seekable VOD view).
        liveSyncDurationCount: 3,
        backBufferLength: 12,
      });
      hls.on(Hls.Events.FRAG_BUFFERED, (_evt, data) => {
        if (cancelled) return;
        setAcquiring(false);
        // Measure the ACTUAL delivered bitrate off each fragment (real VBR, not the
        // declared cap), averaged over a short rolling window.
        const bytes = data.frag?.stats?.total ?? data.stats?.total;
        const seconds = data.frag?.duration;
        if (bytes && seconds && seconds > 0) {
          samples.push({ bytes, seconds });
          if (samples.length > 16) samples.shift();
          setKbps(rollingKbps(samples));
        }
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data.fatal || cancelled) return;
        // A fatal error tears this instance down and rebuilds after a beat, so the
        // monitor self-heals. LL-HLS failing (webrtc-encode down, warm-up, or a push
        // stream with no LL path) falls back to the S3 live HLS; the S3 tier then
        // rebuilds itself on a warm-up 404 or transient drop.
        hls?.destroy();
        hls = null;
        // hls.js can fire several fatal errors around a teardown; schedule at most
        // ONE rebuild at a time so we never orphan a timer or run two concurrent
        // players on the same element. The timer clears its own handle before it
        // rebuilds, so the next cycle can schedule again.
        if (retry) return;
        setAcquiring(true);
        retry = setTimeout(() => {
          retry = null;
          build(liveHlsUrl(streamId!), "s3");
        }, tier === "llhls" ? 1000 : 2000);
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    };

    // Prefer LL-HLS (~2-4s, part-based) from webrtc-encode; the token rides the path.
    // A fresh mint yields the token; any mint failure drops straight to the S3 live
    // HLS (the ~15s safety net served by the live-ingest proxy).
    (async () => {
      let llUrl: string | null = null;
      try {
        const pb = await mintPlaybackUrl(streamId!);
        const whep = whepUrlFromPlayback(streamId!, pb.url);
        if (whep) llUrl = llHlsUrlFromWhep(whep);
      } catch {
        /* fall through to the S3 live HLS */
      }
      if (cancelled) return;
      build(llUrl ?? liveHlsUrl(streamId!), llUrl ? "llhls" : "s3");
    })();

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      hls?.destroy();
      hls = null;
    };
  }, [canPlay, streamId, mode]);

  return (
    <div
      style={{
        width: 268,
        flex: "none",
        position: "relative",
        aspectRatio: "16 / 9",
        borderRadius: "var(--r-md)",
        overflow: "hidden",
        border: `1px solid ${onAir ? "rgba(229,51,78,.45)" : "var(--line)"}`,
        background: "var(--bg-void)",
      }}
    >
      {onAir ? (
        <>
          {(canWebrtc || canPlay) ? (
            <video
              ref={videoRef}
              muted
              autoPlay
              playsInline
              // Native controls shown as a fallback: if the custom overlay/autoplay
              // path misbehaves, the operator still has play/pause, seek, volume and
              // fullscreen straight off the browser's own control bar.
              controls
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
            />
          ) : (
            // On air, but this browser can't play HLS inline: show a plain veil
            // rather than a fake preview (R10), the timecode still counts up.
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(140deg,#1B2440,#080B10 70%)" }} />
          )}
          {(canWebrtc || canPlay) && acquiring && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(3,5,6,.72)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: "500 11px var(--font-sans)",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "var(--fg3)",
              }}
            >
              Acquiring signal
            </div>
          )}
          {/* Latency-mode badge: WebRTC (sub-second) vs HLS. Click to force the
              other path (WebRTC auto-falls-back to HLS on failure). */}
          {(canWebrtc || canPlay) && !acquiring && (
            <button
              onClick={() => setMode((m) => (m === "webrtc" ? "hls" : "webrtc"))}
              title={mode === "webrtc" ? "WebRTC (sub-second). Click for HLS." : "HLS. Click to try WebRTC."}
              style={{
                position: "absolute",
                top: 6,
                left: 6,
                padding: "2px 6px",
                borderRadius: "var(--r-sm)",
                border: "1px solid var(--line-strong)",
                background: "rgba(3,5,6,.6)",
                color: mode === "webrtc" ? "var(--blue-400)" : "var(--fg3)",
                font: "600 9px var(--font-sans)",
                letterSpacing: ".08em",
                cursor: "pointer",
              }}
            >
              {mode === "webrtc" ? "⚡ WEBRTC" : "HLS"}
            </button>
          )}
        </>
      ) : failed ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(140deg,#2A1416,#080B10 72%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "0 16px",
            textAlign: "center",
          }}
        >
          <span
            style={{
              font: "600 10px var(--font-sans)",
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "#F2777B",
            }}
          >
            Broadcast failed
          </span>
          <span
            style={{
              font: "400 11px var(--font-sans)",
              lineHeight: 1.4,
              color: "var(--fg2)",
            }}
          >
            {failureReason}
          </span>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(3,5,6,.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            font: "500 11px var(--font-sans)",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "var(--fg3)",
          }}
        >
          No program
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: 9,
          top: 9,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          background: "rgba(3,5,6,.7)",
          borderRadius: "var(--r-xs)",
          // Decorative overlay: never capture clicks, so the native control bar
          // underneath stays fully usable as a fallback.
          pointerEvents: "none",
        }}
      >
        <Dot tone={onAir ? "onair" : "idle"} pulse={onAir} />
        <span
          style={{
            font: "600 9px var(--font-sans)",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "#fff",
          }}
        >
          Program
        </span>
      </div>
      <span
        style={{
          position: "absolute",
          right: 9,
          bottom: 8,
          font: "400 10px var(--font-mono)",
          color: "#fff",
          background: "rgba(3,5,6,.7)",
          padding: "3px 6px",
          borderRadius: "var(--r-xs)",
          pointerEvents: "none",
        }}
      >
        {tc}
      </span>
      {/* Live delivered-bitrate badge (the real VBR figure off the buffered
          fragments), top-right, shown once frames are flowing. */}
      {onAir && kbps != null && (
        <span
          style={{
            position: "absolute",
            right: 9,
            top: 9,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            font: "600 10px var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            color: "#fff",
            background: "rgba(0,0,0,.8)",
            padding: "3px 7px",
            borderRadius: "var(--r-xs)",
            pointerEvents: "none",
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f43f5e" }} />
          {fmtKbps(kbps)}
        </span>
      )}
    </div>
  );
}
